import { useCallback, useRef } from "react";
import {
  Driver,
  DispatchScope,
  Maybe,
  OrderingAlgorithmType,
  Shipment,
} from "@api/graphql/generated/generated-types";
import { useDriverRouteDataProvider } from "../contexts/DriverRouteDataProviderContext";
import { useDriverRouteViewSelectionProvider } from "../contexts/DriverRouteViewSelectionProvider";
import { getEffectiveBaseTime } from "../utils/timeUtils";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import moment from "moment-timezone";

/**
 * Handles ordering algorithm changes and route optimization mutations.
 * Manages abort controller for cancelling in-progress requests.
 *
 * When user changes algorithm via UI tabs, this hook:
 * 1. Updates local component state
 * 2. Notifies parent via onAlgorithmTypeChange callback (for API persistence)
 * 3. Triggers optimization mutation if not Manual mode
 */
export function useRouteOptimization({
  driver,
  selectedShipment,
  selectedStopDate,
  courierId,
  tabOrder,
  OrderingAlgorithmTypes,
  setLoading,
  setErrorMessage,
  setIsValidOrder,
  onAlgorithmTypeChange,
  dispatchScope,
}: {
  driver: Driver | Maybe<Driver> | undefined;
  selectedShipment: Shipment | undefined;
  selectedStopDate: string | undefined;
  courierId: string | null;
  tabOrder: OrderingAlgorithmType[];
  OrderingAlgorithmTypes: OrderingAlgorithmType[];
  setLoading?: (value: boolean) => void;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string | null>>;
  setIsValidOrder: (value: boolean) => void;
  // Callback to notify parent of algorithm changes (for backend API calls)
  onAlgorithmTypeChange?: (value: OrderingAlgorithmType) => void;
  // Sent as-is on optimize; undefined means cube's default, SINGLE_ORDER.
  dispatchScope?: DispatchScope;
}) {
  const { showToast } = useCourieStore();
  const { optimizeRouteForDispatchMutation } = useDriverRouteDataProvider();
  const {
    selectedBaseTime,
    setSelectedBaseTime,
    setSelectedOrderingAlgorithmType,
  } = useDriverRouteViewSelectionProvider();

  const isRefetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleOrderingAlgorithmChange = useCallback(
    (
      index: number,
      baseTimeMoment?: moment.Moment,
      startLocationId?: string,
      endLocationId?: string
    ) => {
      if (
        !selectedShipment ||
        !driver ||
        index < 0 ||
        index >= OrderingAlgorithmTypes.length
      ) {
        return;
      }

      if (isRefetchingRef.current) {
        // Cancel the previous API call if it's still in progress
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }

      const effectiveBaseTime = baseTimeMoment ?? selectedBaseTime;
      const adjustedBaseTime = getEffectiveBaseTime(
        effectiveBaseTime,
        selectedStopDate
      );

      // Update state if baseTimeMoment was provided
      if (baseTimeMoment) {
        setSelectedBaseTime(adjustedBaseTime);
      }

      const baseTimeInSeconds = adjustedBaseTime.unix();

      const selectedOrderingAlgorithmType = tabOrder[index];
      // Update local state
      setSelectedOrderingAlgorithmType(selectedOrderingAlgorithmType);
      // Notify parent component (for backend API calls and multi-day persistence)
      onAlgorithmTypeChange?.(selectedOrderingAlgorithmType);

      if (selectedOrderingAlgorithmType !== OrderingAlgorithmType.Manual) {
        setLoading?.(true);
        isRefetchingRef.current = true;
        // Create a new AbortController for the upcoming request
        const newAbortController = new AbortController();
        abortControllerRef.current = newAbortController;
        optimizeRouteForDispatchMutation({
          variables: {
            courierId: courierId,
            newShipmentId: selectedShipment.id,
            driverId: driver.id,
            orderingAlgorithmType: selectedOrderingAlgorithmType,
            stopDate: selectedStopDate ? selectedStopDate : undefined,
            baseTime: baseTimeInSeconds,
            startLocationId:
              selectedOrderingAlgorithmType ===
              OrderingAlgorithmType.Reoptimized
                ? startLocationId
                : undefined,
            endLocationId:
              selectedOrderingAlgorithmType ===
              OrderingAlgorithmType.Reoptimized
                ? endLocationId
                : undefined,
            dispatchScope,
          },
          context: {
            fetchOptions: {
              signal: newAbortController.signal,
            },
          },
        })
          .then(() => {
            if (!isRefetchingRef.current) {
              setLoading?.(false);
              setErrorMessage?.(null);
              setIsValidOrder(true);
            }
          })
          .catch((error) => {
            if (error.message.includes("aborted")) {
              return;
            } else {
              showErrorToast(error, showToast);
              setLoading?.(false);
              setErrorMessage?.("Something went wrong");
              setIsValidOrder(false);
            }
          })
          .finally(() => {
            isRefetchingRef.current = false;
            abortControllerRef.current = null;
          });
      }
    },
    [
      selectedShipment,
      driver,
      OrderingAlgorithmTypes.length,
      selectedBaseTime,
      selectedStopDate,
      setSelectedBaseTime,
      tabOrder,
      setSelectedOrderingAlgorithmType,
      onAlgorithmTypeChange,
      setLoading,
      setIsValidOrder,
      setErrorMessage,
      optimizeRouteForDispatchMutation,
      courierId,
      showToast,
      dispatchScope,
    ]
  );

  return {
    handleOrderingAlgorithmChange,
    isRefetching: isRefetchingRef.current,
  };
}
